import { StreamingClient } from "axon-provider-kit/client";
import { ToolChoice, createMessage, isAIProvider } from "axon-provider-kit/core";
import type { LeagueAthleteFieldDefinition } from "@/models/fieldDefinition";
import {
  isWebAIProviderSupported,
  resolvedMaxTokens,
  type SBotAIPersonality,
} from "@/features/ai/services/aiConfigService";
import { PROXY_API_KEY_PLACEHOLDER, proxyTransport } from "@/features/ai/services/proxyTransport";
import {
  deterministicProposals,
  mappingSystemPrompt,
  mappingTool,
  mappingUserMessage,
  mergeMappingProposals,
  parseAIProposalJSON,
  type ExternalMappingProposal,
  type ExternalSourceSchema,
} from "./externalDataLogic";

export async function aiEnhancedExternalMappingProposals({
  schema,
  existingFields,
  personality,
  deterministic = deterministicProposals(schema, existingFields),
}: {
  schema: ExternalSourceSchema;
  existingFields: LeagueAthleteFieldDefinition[];
  personality: SBotAIPersonality;
  deterministic?: ExternalMappingProposal[];
}): Promise<{ proposals: ExternalMappingProposal[]; aiNotice?: string }> {
  if (!isAIProvider(personality.providerID) || !isWebAIProviderSupported(personality.providerID)) {
    return { proposals: deterministic, aiNotice: `AI mapping suggestions are not available for ${personality.providerID}.` };
  }

  const request = {
    system: mappingSystemPrompt(existingFields),
    user: mappingUserMessage(schema, deterministic),
    personality,
  };

  try {
    const raw = await proposeMappings(request);
    return { proposals: mergeMappingProposals(deterministic, parseAIProposalJSON(raw, schema, existingFields)) };
  } catch (firstError) {
    try {
      const repaired = await proposeMappings({
        ...request,
        user: `${request.user}\n\nYour previous response could not be parsed as the required JSON. Respond again using ONLY the propose_athlete_field_mappings tool with valid arguments.`,
      });
      return { proposals: mergeMappingProposals(deterministic, parseAIProposalJSON(repaired, schema, existingFields)) };
    } catch {
      const message = firstError instanceof Error ? firstError.message : "AI suggestions were unavailable.";
      return { proposals: deterministic, aiNotice: `AI suggestions were unavailable (${message}). Automatic matches are shown instead.` };
    }
  }
}

async function proposeMappings({
  system,
  user,
  personality,
}: {
  system: string;
  user: string;
  personality: SBotAIPersonality;
}): Promise<string> {
  if (!isAIProvider(personality.providerID)) throw new Error(`Unsupported provider '${personality.providerID}'.`);
  const client = new StreamingClient({ transport: proxyTransport });
  let toolArguments: string | undefined;
  let fullText = "";
  for await (const event of client.stream({
    messages: [createMessage({ role: "user", content: user })],
    provider: personality.providerID,
    model: personality.modelID,
    apiKey: PROXY_API_KEY_PLACEHOLDER,
    system,
    maxTokens: resolvedMaxTokens(personality, 4096),
    tools: [mappingTool()],
    toolChoice: ToolChoice.specific("propose_athlete_field_mappings"),
  })) {
    switch (event.type) {
      case "toolCallComplete":
        if (event.toolCall.name === "propose_athlete_field_mappings") toolArguments = event.toolCall.arguments;
        break;
      case "toolResultRequired": {
        const call = event.toolCalls.find((entry) => entry.name === "propose_athlete_field_mappings");
        if (call) toolArguments = call.arguments;
        break;
      }
      case "textDelta":
        fullText += event.text;
        break;
      case "error":
        throw event.error;
      default:
        break;
    }
  }
  if (toolArguments) return toolArguments;
  const extracted = extractJSONObject(fullText);
  if (extracted) return extracted;
  throw new Error("The AI returned no mapping proposals.");
}

function extractJSONObject(text: string): string | undefined {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start < 0 || end < start) return undefined;
  const candidate = text.slice(start, end + 1);
  try {
    JSON.parse(candidate);
    return candidate;
  } catch {
    return undefined;
  }
}
